/**
 * Pure transformations on a parcel plan. Every one takes a plan and returns a new plan, touching
 * nothing in place.
 *
 * These were previously ~50 lines of updater closures duplicated verbatim between the split flow and
 * the parcel editor, plus one (`expand`) that mutated React state in place through a shallow copy.
 * Being pure functions they are directly testable, which the closures were not.
 *
 * `bindParcelEdits` adapts them to whoever owns the plan - the useParcelPlan hook for the flows that
 * hold their own state, or an external setter for the in-item editor where the plan lives on
 * item.Consignment.
 */
import { ConsignmentApiModel, ConsignmentParcelItemApiModel, Dimensions } from '~/lib/api/smartsend/types';
import { LocalUnitsEntry } from '../helpers/DimensionUnits';
import { flattenToUnits } from './allocate';
import { tinyId } from '../helpers/uid';
import { moveRowsWeight } from './parcelWeight';

export type ContentTextField = 'Description' | 'Sku' | 'TariffCode' | 'CountryOfManufacture';
export type ContentNumberField = 'Quantity' | 'Value';

const allContents = (plan: ConsignmentApiModel): ConsignmentParcelItemApiModel[] =>
  // `?? []` - flatMap over an undefined Contents yields [undefined], not [].
  plan.Parcels.flatMap((p) => p.Contents ?? []);

/** A parcel with nothing in it is not a parcel - the editor never shows empties. */
const dropEmptyParcels = (plan: ConsignmentApiModel): ConsignmentApiModel => ({
  Parcels: plan.Parcels.filter((p) => p.Contents?.length > 0)
});

const mapContents = (
  plan: ConsignmentApiModel,
  fn: (row: ConsignmentParcelItemApiModel) => ConsignmentParcelItemApiModel
): ConsignmentApiModel => ({
  Parcels: plan.Parcels.map((p) => ({ ...p, Contents: p.Contents.map(fn) }))
});

export const setContentText = (
  plan: ConsignmentApiModel,
  uid: string,
  field: ContentTextField,
  value: string
): ConsignmentApiModel => mapContents(plan, (row) => (row.UID === uid ? { ...row, [field]: value } : row));

export const setContentNumber = (
  plan: ConsignmentApiModel,
  uid: string,
  field: ContentNumberField,
  value: number
): ConsignmentApiModel => mapContents(plan, (row) => (row.UID === uid ? { ...row, [field]: value } : row));

/**
 * `localUnitsEntry` records what the user typed when they typed it in a non-metric unit. It is stored
 * per parcel rather than per item because it describes one specific set of dimensions - see
 * ConsignmentParcelApiModel.LocalUnits. Passing it as undefined clears any previous entry, which is
 * correct for a metric edit: a stale imperial original would contradict the new metric value.
 */
export const setParcelDimensions = (
  plan: ConsignmentApiModel,
  parcelUID: string,
  dimensions: Dimensions,
  localUnitsEntry?: LocalUnitsEntry
): ConsignmentApiModel => ({
  Parcels: plan.Parcels.map((p) =>
    p.UID === parcelUID ? { ...p, Dimensions: { ...dimensions }, LocalUnits: localUnitsEntry } : p
  )
});

/** A row the user typed themselves, with no order line behind it. */
export const addManualContent = (plan: ConsignmentApiModel, parcelUID: string): ConsignmentApiModel => ({
  Parcels: plan.Parcels.map((p) =>
    p.UID === parcelUID
      ? {
          ...p,
          Contents: [...p.Contents, { Id: null, UID: tinyId(), Description: '', Quantity: 1, Value: 0, Manual: true }]
        }
      : p
  )
});

/** Refuses to empty a parcel - the last row in a parcel cannot be removed. */
export const removeContent = (plan: ConsignmentApiModel, parcelUID: string, itemUID: string): ConsignmentApiModel => ({
  Parcels: plan.Parcels.map((p) =>
    p.UID === parcelUID && p.Contents.length > 1
      ? { ...p, Contents: p.Contents.filter((row) => row.UID !== itemUID) }
      : p
  )
});

/**
 * Splits a quantity-N row into N quantity-1 rows in place, so the units can be moved between parcels
 * individually. Value is divided with the usual first-row-takes-the-remainder convention.
 */
export const expandContent = (plan: ConsignmentApiModel, uid: string): ConsignmentApiModel => ({
  Parcels: plan.Parcels.map((p) => ({
    ...p,
    Contents: p.Contents.flatMap((row) => (row.UID === uid && row.Quantity > 1 ? flattenToUnits([row]) : [row]))
  }))
});

/** Moves the selected rows into an existing parcel, pruning any parcel left empty. */
export const moveContentsToParcel = (
  plan: ConsignmentApiModel,
  targetParcelUID: string,
  contentUIDs: string[]
): ConsignmentApiModel => {
  const moving = allContents(plan).filter((row) => contentUIDs.includes(row.UID));

  return dropEmptyParcels({
    Parcels: plan.Parcels.map((p) => {
      const kept = p.Contents.filter((row) => !contentUIDs.includes(row.UID));
      return { ...p, Contents: p.UID === targetParcelUID ? kept.concat(moving) : kept };
    })
  });
};

/**
 * Groups the selected rows into a parcel of their own.
 *
 * If an addable parcel already holds exactly the selection and nothing else, that parcel is reused
 * rather than creating an identical new one beside it.
 */
export const moveContentsToNewParcel = (
  plan: ConsignmentApiModel,
  contentUIDs: string[],
  dimensions: Dimensions
): ConsignmentApiModel => {
  const moving = allContents(plan).filter((row) => contentUIDs.includes(row.UID));

  const existingTarget = plan.Parcels.find((p) => p.CanAdd && p.Contents.every((row) => contentUIDs.includes(row.UID)));

  const withTarget: ConsignmentApiModel = existingTarget
    ? { Parcels: [...plan.Parcels] }
    : {
        Parcels: [
          ...plan.Parcels,
          // Weightless on purpose: the template supplies the box SIZE, but its weight is whatever lands in
          // it - moveRowsWeight credits the share that moved. Seeding the shipment weight here and then
          // crediting a share on top declared the same weight twice.
          { Dimensions: { ...dimensions, Weight: 0 }, UID: tinyId(), Id: null, CanAdd: true, Contents: [] }
        ]
      };

  const targetUID = existingTarget?.UID ?? withTarget.Parcels[withTarget.Parcels.length - 1].UID;

  return dropEmptyParcels({
    Parcels: withTarget.Parcels.map((p) => ({
      ...p,
      Contents: p.UID === targetUID ? moving : p.Contents.filter((row) => !contentUIDs.includes(row.UID))
    }))
  });
};

/**
 * Whether dragging one whole content row into `targetParcelUID` is a legal move.
 *
 * Reducing the parcel count is allowed, including all the way down: gathering everything into one
 * parcel is a thing customers want, and the caller turns that outcome into the canonical
 * single-parcel state rather than leaving a one-parcel consignment behind. See
 * ConsignmentDetails.onDragRowToParcel - a consignment of one parcel is NOT the same state as a
 * single-parcel shipment, so it must not be written back as one.
 *
 * Deliberately does NOT consult `CanAdd`. That is a grouping concept - whether a shipment can absorb
 * another item's lines - not per-parcel capacity, and it is hard-coded false on other surfaces.
 */
export const canDropRowIntoParcel = (
  plan: ConsignmentApiModel,
  contentUID: string,
  targetParcelUID: string
): boolean => {
  // Under two parcels there is nowhere to drag to.
  if (!plan?.Parcels?.length || plan.Parcels.length < 2) {
    return false;
  }

  // More than one holder cannot happen while UIDs are minted per load, but moveContentsToParcel
  // filters by UID across the whole plan, so a duplicate would move both rows. Refuse instead.
  const holders = plan.Parcels.filter((p) => p.Contents?.some((row) => row.UID === contentUID));

  if (holders.length !== 1) {
    return false;
  }

  if (holders[0].UID === targetParcelUID) {
    return false;
  }

  return plan.Parcels.some((p) => p.UID === targetParcelUID);
};

/**
 * Drags one whole content row - all of its Quantity - into another parcel.
 *
 * Returns the plan UNCHANGED BY REFERENCE when the move is refused, and never null: onUpdate(null) is
 * sendAsSingle (ConsignmentDetails.tsx), so a null here would destroy the consignment as a side effect
 * of a refusal. Collapsing to one parcel is signalled by the returned plan having a single parcel,
 * which the caller translates - it is not signalled by null.
 *
 * Takes no dimensions. The moved row's share of weight goes with it, so the box it left stops declaring
 * it and the sum of the parcels - which is what GetItemWeight treats as the shipment weight - comes out
 * unchanged, leaving the quote alone. See moveRowWeight.
 */
export const dragRowToParcel = (
  plan: ConsignmentApiModel,
  contentUID: string,
  targetParcelUID: string
): ConsignmentApiModel => {
  if (!canDropRowIntoParcel(plan, contentUID, targetParcelUID)) {
    return plan;
  }

  const moved = moveContentsToParcel(plan, targetParcelUID, [contentUID]);

  return moveRowsWeight(plan, moved, [contentUID]);
};

/**
 * What a drop should do to the item, rather than just to the plan.
 *
 * 'SEND_AS_SINGLE' exists because a consignment holding one parcel is NOT the same state as a
 * single-parcel shipment: it would highlight 1x in the picker while rendering the multi-parcel copy,
 * and would persist a one-parcel ConsignmentMeta and send it to /quote where a single-box item sends
 * none. Gathering everything into one parcel has to become the same state as picking 1x.
 */
export type RowDragOutcome =
  { kind: 'REFUSED' } | { kind: 'SEND_AS_SINGLE' } | { kind: 'PLAN'; plan: ConsignmentApiModel };

/** Resolves a drop to the action it implies. Follows resolveConsignmentTarget in commit.ts. */
export const resolveRowDrag = (
  plan: ConsignmentApiModel,
  contentUID: string,
  targetParcelUID: string
): RowDragOutcome => {
  const next = dragRowToParcel(plan, contentUID, targetParcelUID);

  if (next === plan) {
    return { kind: 'REFUSED' };
  }

  return next.Parcels.length < 2 ? { kind: 'SEND_AS_SINGLE' } : { kind: 'PLAN', plan: next };
};

/**
 * Changing what is IN a parcel - the customs fields, extra rows, per-parcel dimensions.
 * Independent of rearranging: the flow that resolves rows back to their order lines allows
 * rearranging but shows the fields read-only, and the split flow is the reverse.
 */
export interface ParcelContentActions {
  setText(uid: string, field: ContentTextField, value: string): void;
  setNumber(uid: string, field: ContentNumberField, value: number): void;
  setParcelDimensions(parcelUID: string, dimensions: Dimensions, localUnitsEntry?: LocalUnitsEntry): void;
  addManualContent(parcelUID: string): void;
  removeContent(parcelUID: string, itemUID: string): void;
}

/** Moving contents BETWEEN parcels, and splitting a row so its units can be moved separately. */
export interface ParcelArrangeActions {
  expandContent(uid: string): void;
  moveToParcel(targetParcelUID: string, contentUIDs: string[]): void;
  moveToNewParcel(contentUIDs: string[]): void;
}

export type ParcelEditActions = ParcelContentActions & ParcelArrangeActions;

/**
 * The weight half of a move: the rows that moved take their share of weight with them.
 *
 * Shared with the drag (see dragRowToParcel) so the two routes into a move cannot disagree - previously
 * this rehomed weight only when a parcel was pruned, so a move out of a box that still had contents left
 * that box declaring weight it no longer held.
 */
const carryRowWeights = (
  before: ConsignmentApiModel,
  after: ConsignmentApiModel,
  movedUIDs: string[]
): ConsignmentApiModel => moveRowsWeight(before, after, movedUIDs);

/**
 * Binds the pure transformations to whoever owns the plan. `read` is a getter rather than a value so
 * the actions always operate on the current plan even when several fire in one tick.
 */
export const bindParcelEdits = (
  read: () => ConsignmentApiModel,
  write: (plan: ConsignmentApiModel) => void,
  newParcelDimensions: () => Dimensions
): ParcelEditActions => ({
  setText: (uid, field, value) => write(setContentText(read(), uid, field, value)),
  setNumber: (uid, field, value) => write(setContentNumber(read(), uid, field, value)),
  setParcelDimensions: (parcelUID, dimensions, localUnitsEntry) =>
    write(setParcelDimensions(read(), parcelUID, dimensions, localUnitsEntry)),
  addManualContent: (parcelUID) => write(addManualContent(read(), parcelUID)),
  removeContent: (parcelUID, itemUID) => write(removeContent(read(), parcelUID, itemUID)),
  expandContent: (uid) => write(expandContent(read(), uid)),
  moveToParcel: (targetParcelUID, contentUIDs) => {
    const plan = read();
    write(carryRowWeights(plan, moveContentsToParcel(plan, targetParcelUID, contentUIDs), contentUIDs));
  },
  moveToNewParcel: (contentUIDs) => {
    const plan = read();
    write(carryRowWeights(plan, moveContentsToNewParcel(plan, contentUIDs, newParcelDimensions()), contentUIDs));
  }
});
